import { useEffect, useRef, useState, Fragment, type RefObject } from 'react';
import { Link, NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  AnimatePresence,
  animate,
  motion,
  useMotionValue,
  useReducedMotion,
  useTransform,
} from 'framer-motion';
import {
  ArrowLeft,
  Bell,
  ChevronDown,
  Eye,
  KeyRound,
  LogOut,
  Menu,
  Moon,
  Pin,
  QrCode,
  Sun,
  UserRound,
  X,
} from 'lucide-react';
import { useAuth } from '@/shared/lib/auth';
import { isMasterEmail } from '@/shared/lib/navigation';
import { useBrand, useSyncBrandFromPassbook } from '@/shared/lib/brand';
import { isNpsSession } from '@/shared/lib/nps-session';
import { useTheme } from '@/shared/lib/theme';
import { buildNavigation, withUserPageFlags } from '@/shared/lib/navigation';
import { clearLastPassbookId, getLastPassbookId } from '@/shared/lib/last-passbook';
import { firstPassbookId } from '@/shared/lib/passbook-access';
import { usePassbookQuery } from '@/shared/lib/passbook';
import {
  getSidebarPinned,
  setSidebarPinned,
  SIDEBAR_WIDTH_COLLAPSED,
  SIDEBAR_WIDTH_EXPANDED,
} from '@/shared/lib/sidebar-collapsed';
import { SIDEBAR_SPRING, SidebarMotionProvider, useSidebarMotion } from '@/shared/lib/sidebar-motion';
import { initials } from '@/shared/lib/format';
import { passbookLogoUrl } from '@/shared/lib/passbook-utils';
import { SidebarQuota } from '@/features/channel-quota/ui/sidebar-quota';
import { useUnreadNotices } from '@/features/notifications/model/use-unread-notices';
import { NoticesPopover, UnreadNoticesBadge } from '@/features/notifications/ui/notices-popover';
import { QuotaNoticeSync } from '@/features/notifications/ui/quota-notice-sync';
import { cn } from '@/shared/lib/cn';
import type { NavItem, Passbook } from '@/shared/types';
import { SidebarBrand } from '@/shared/components/sidebar-brand';
import { BrandSwitcher } from './brand-switcher';
import { useSidebarHover } from './use-sidebar-hover';
import { RouteTransition } from '@/shared/components/route-transition';
import { Button } from '@/shared/ui/button';
import { Switch } from '@/shared/ui/switch';
import { Avatar, AvatarFallback } from '@/shared/ui/avatar';
import { PopoverAnchor } from '@/shared/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { useNpsMe } from '@/features/nps/api/use-nps';
import { isNpsMasterEmail } from '@/features/nps/hooks/use-nps-access';
import {
  AdminInspectBackButton,
  AdminInspectConfigureButton,
  AdminInspectHeaderMark,
} from '@/shared/components/admin-inspect-banner';

/** Título da seção; no trilho vira um divisor curto — mesma altura nos dois estados. */
function NavSectionHeader({ title, first }: { title: string; first: boolean }) {
  const { reveal, rail } = useSidebarMotion();
  return (
    <div
      className={cn(
        'relative px-3 pb-1 text-[11px] font-semibold uppercase leading-4 tracking-wider text-muted-foreground/70',
        first ? 'pt-0' : 'pt-4',
      )}
    >
      <motion.span
        className="sidebar-label-fade block overflow-hidden whitespace-nowrap"
        style={{ opacity: reveal }}
      >
        {title}
      </motion.span>
      {first ? null : (
        <motion.span
          aria-hidden
          className="absolute left-2 top-1/2 h-px w-6 bg-border/70"
          style={{ opacity: rail }}
        />
      )}
    </div>
  );
}

/**
 * Menu lateral com a mesma estrutura aberto e no trilho: o ícone fica numa
 * coluna fixa de 40px e o rótulo é revelado pela borda. Nenhum item muda de
 * altura entre os estados — o que está sob o ponteiro no trilho continua lá
 * quando a sidebar abre no hover.
 */
function SidebarNav({
  items,
  indexPath,
  onNavigate,
}: {
  items: NavItem[];
  indexPath?: string;
  onNavigate?: () => void;
}) {
  const location = useLocation();
  const { progress, reveal } = useSidebarMotion();
  // Subitem: recuo de pl-9 aberto; no trilho o ícone volta para o centro da coluna.
  const nestedIndent = useTransform(progress, [0, 1], [0, 25]);
  const groupIconOpacity = useTransform(progress, [0, 1], [0.45, 1]);

  const renderLink = (item: NavItem, nested?: boolean) => {
    const Icon = item.icon;
    const isParentOfAnother = items.some(
      (o) => o.to !== item.to && o.to.startsWith(`${item.to}/`),
    );
    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.to === indexPath || isParentOfAnother}
        onClick={onNavigate}
        className="group relative block w-full"
      >
        {({ isActive }) => (
          <motion.span
            className={cn(
              'relative flex items-center overflow-hidden rounded-xl py-2.5 pr-3 text-sm font-medium transition-colors',
              isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
            )}
            style={nested ? { paddingLeft: nestedIndent } : undefined}
          >
            {isActive && (
              <span className="bg-primary/12 absolute inset-0 rounded-xl ring-1 ring-inset ring-primary/20" />
            )}
            <span className="relative flex w-10 shrink-0 justify-center">
              <Icon className="size-[18px]" />
            </span>
            <motion.span
              className="sidebar-label-fade relative min-w-0 flex-1 overflow-hidden whitespace-nowrap"
              style={{ opacity: reveal }}
            >
              {item.name}
            </motion.span>
          </motion.span>
        )}
      </NavLink>
    );
  };

  return (
    <nav className="flex flex-col gap-1">
      {items.map((item, index) => {
        const opensSection = Boolean(item.section) && item.section !== items[index - 1]?.section;
        const divider = opensSection ? (
          <NavSectionHeader title={item.section!} first={index === 0} />
        ) : null;

        if (item.children && item.children.length > 0) {
          const childActive = item.children.some((c) => location.pathname.startsWith(c.to));
          const Icon = item.icon;

          return (
            <Fragment key={item.to}>
              {divider}
              <div className="flex flex-col gap-0.5">
                <div
                  className={cn(
                    'flex items-center overflow-hidden rounded-xl py-2 pr-3 text-sm font-medium',
                    childActive ? 'text-primary' : 'text-muted-foreground',
                  )}
                >
                  <motion.span className="flex w-10 shrink-0 justify-center" style={{ opacity: groupIconOpacity }}>
                    <Icon className="size-[18px]" />
                  </motion.span>
                  <motion.span
                    className="sidebar-label-fade min-w-0 flex-1 overflow-hidden whitespace-nowrap"
                    style={{ opacity: reveal }}
                  >
                    {item.name}
                  </motion.span>
                </div>
                <div className="flex flex-col gap-0.5">
                  {item.children.map((child) => renderLink(child, true))}
                </div>
              </div>
            </Fragment>
          );
        }

        return (
          <Fragment key={item.to}>
            {divider}
            {renderLink(item)}
          </Fragment>
        );
      })}
    </nav>
  );
}

function UserMenu({ collapsed, showNotices }: { collapsed?: boolean; showNotices: boolean }) {
  const { user, logout } = useAuth();
  const { resolvedTheme, toggle } = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [noticesOpen, setNoticesOpen] = useState(false);
  // O item Avisos só marca a intenção; o popover abre em onCloseAutoFocus, quando o
  // dropdown já desmontou. Aberto no onSelect, a desmontagem do menu (foco de volta
  // ao gatilho, que é a âncora do popover) o fechava em seguida.
  const openingNotices = useRef(false);
  const storedUnread = useUnreadNotices();
  // Avisos são de cota de e-mail/SMS: sem um desses canais (só push, por exemplo) não aparecem.
  const unread = showNotices ? storedUnread : 0;
  const { reveal } = useSidebarMotion();
  if (!user) return null;

  const isDark = resolvedTheme === 'dark';

  const go = (path: string) => {
    if (pathname !== path) navigate(path);
  };

  // O painel de avisos ancora neste botão (PopoverAnchor): abre do item "Avisos" do menu.
  const trigger = (
    <PopoverAnchor asChild>
      {/* pl-0.5: o avatar (36px) fica centrado na coluna de 40px dos ícones, aberto e no trilho. */}
      <button
        type="button"
        data-sidebar-hold={noticesOpen || undefined}
        className="flex w-full items-center gap-3 rounded-xl py-1 pl-0.5 pr-2 text-left transition-colors hover:bg-accent/60"
      >
        <span className="relative shrink-0">
          <Avatar className="size-9">
            <AvatarFallback className="text-xs">{initials(user.name)}</AvatarFallback>
          </Avatar>
          <UnreadNoticesBadge count={unread} className="absolute -right-1 -top-1 h-4 min-w-4 px-1" />
        </span>
        <motion.span className="flex min-w-0 flex-1 items-center gap-3" style={{ opacity: reveal }}>
          <span className="min-w-0 flex-1">
            <span className="sidebar-label-fade block overflow-hidden whitespace-nowrap text-sm font-medium">
              {user.name}
            </span>
            <span className="sidebar-label-fade block overflow-hidden whitespace-nowrap text-xs text-muted-foreground">
              {user.role}
            </span>
          </span>
          <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
        </motion.span>
      </button>
    </PopoverAnchor>
  );

  return (
    <NoticesPopover
      open={noticesOpen}
      onOpenChange={setNoticesOpen}
      side={collapsed ? 'right' : 'top'}
      align={collapsed ? 'start' : 'end'}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
        <DropdownMenuContent
          align={collapsed ? 'start' : 'end'}
          side={collapsed ? 'right' : 'top'}
          className="w-60"
          onCloseAutoFocus={(event) => {
            if (!openingNotices.current) return;
            openingNotices.current = false;
            event.preventDefault();
            setNoticesOpen(true);
          }}
        >
          <DropdownMenuLabel className="truncate">{user.email}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {showNotices ? (
            <DropdownMenuItem
              className="justify-between"
              onSelect={() => {
                openingNotices.current = true;
              }}
            >
              <span className="flex items-center gap-2">
                <Bell /> Avisos
              </span>
              <UnreadNoticesBadge count={unread} />
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem onSelect={() => go('/conta')}>
            <UserRound /> Minha conta
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => go('/conta/senha')}>
            <KeyRound /> Alterar senha
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => go('/conta/qr-code')}>
            <QrCode /> Meu QR-Code
          </DropdownMenuItem>
          <DropdownMenuItem
            className="justify-between"
            onSelect={(e) => {
              e.preventDefault();
              toggle();
            }}
          >
            <span className="flex items-center gap-2">
              {isDark ? <Moon /> : <Sun />} Tema escuro
            </span>
            <Switch checked={isDark} className="pointer-events-none" />
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            destructive
            onSelect={async () => {
              await logout();
              navigate('/login', { replace: true });
            }}
          >
            <LogOut /> Sair
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </NoticesPopover>
  );
}

/** Fixar/desafixar: pino em pé e preenchido quando fixada, inclinado e vazado quando não. */
function SidebarPinButton({ pinned, onToggle }: { pinned: boolean; onToggle: () => void }) {
  return (
    <Button
      variant="ghost"
      size="icon"
      className={cn('size-8 shrink-0', pinned ? 'text-primary hover:text-primary' : 'text-muted-foreground')}
      onClick={onToggle}
      aria-pressed={pinned}
      aria-label={pinned ? 'Desafixar menu lateral' : 'Fixar menu lateral'}
      title={pinned ? 'Desafixar menu' : 'Fixar menu'}
    >
      <motion.span
        className="grid place-items-center"
        initial={false}
        animate={{ rotate: pinned ? 0 : 45 }}
        transition={SIDEBAR_SPRING}
      >
        <Pin
          fill="currentColor"
          fillOpacity={pinned ? 1 : 0}
          className="transition-[fill-opacity] duration-200"
        />
      </motion.span>
    </Button>
  );
}

function SidebarHeader({
  pinned,
  onTogglePin,
  showPin = true,
}: {
  pinned: boolean;
  onTogglePin: () => void;
  showPin?: boolean;
}) {
  const { reveal } = useSidebarMotion();
  // pl-0.5: o ícone da marca (36px) alinha com a coluna dos ícones do menu.
  return (
    <div className="flex shrink-0 items-center pl-0.5 pt-2">
      <SidebarBrand />
      {showPin ? (
        <motion.div className="ml-auto pl-2" style={{ opacity: reveal }}>
          <SidebarPinButton pinned={pinned} onToggle={onTogglePin} />
        </motion.div>
      ) : null}
    </div>
  );
}

export function AppShell() {
  const { user, realUser, viewAs, stopViewAs, authSource } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { idPassbook: urlPassbookId } = useParams<{ idPassbook?: string }>();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarPinned, setSidebarPinnedState] = useState(getSidebarPinned);
  const passbookCache = useRef<{ id: string; data: Passbook } | null>(null);
  const mobileNavScrollRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const sidebarHover = useSidebarHover(sidebarRef);
  // Fixada: sempre aberta, ocupando espaço. Desafixada: trilho que abre por cima no hover.
  const sidebarExpanded = sidebarPinned || sidebarHover.open;
  const sidebarFloating = sidebarExpanded && !sidebarPinned;
  const reduceMotion = useReducedMotion();
  const sidebarWidth = useMotionValue(sidebarExpanded ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED);
  const sidebarProgress = useTransform(sidebarWidth, [SIDEBAR_WIDTH_COLLAPSED, SIDEBAR_WIDTH_EXPANDED], [0, 1]);

  // Uma mola só na largura; o resto da sidebar segue o progresso dela. Hover que
  // entra e sai no meio do caminho inverte a mola com a velocidade que ela tinha.
  useEffect(() => {
    const controls = animate(
      sidebarWidth,
      sidebarExpanded ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED,
      reduceMotion ? { duration: 0 } : SIDEBAR_SPRING,
    );
    return () => controls.stop();
  }, [sidebarExpanded, reduceMotion, sidebarWidth]);

  const isLegacySession = authSource === 'legacy';
  const isNpsArea = isNpsSession(authSource) && location.pathname.startsWith('/nps');
  const isAdmin = user?.role === 'Admin';
  const isOwner = user?.role === 'Owner';
  // Owner: menu da loja só enquanto a URL é /loja/:id (no home carregava
  // id_passbooks e o menu virava WhatsApp/gerais da loja). Admin: a loja
  // inspecionada fica guardada e segue no menu até deslogar ou abrir outra.
  const inspectsStoreOnlyWhenInUrl = isOwner;
  // Canvas em tela cheia: construtor de segmentação e o editor de template
  // de e-mail. Altura do viewport, sem scroll de página e sem o max-w-7xl.
  const isFullBleedCanvas =
    /\/segmentacoes\/(nova|[^/]+\/editar)$/.test(location.pathname) ||
    /\/(?:email-marketing|plataforma)\/templates\/(novo|[^/]+\/editar)$/.test(location.pathname);

  // Admin fora de /loja/:id: a última loja inspecionada (sessionStorage).
  // Demais roles: a última loja / id_passbooks (ex.: /conta). Owner: nada.
  const fallbackPassbookId = inspectsStoreOnlyWhenInUrl
    ? undefined
    : isAdmin
      ? getLastPassbookId()
      : (getLastPassbookId() ?? firstPassbookId(user));
  const passbookIdForFetch =
    isLegacySession && !isNpsSession(authSource)
      ? (urlPassbookId ?? fallbackPassbookId ?? '')
      : '';

  // Owner: saiu de /loja/:id → limpa a loja persistida.
  useEffect(() => {
    if (!inspectsStoreOnlyWhenInUrl) return;
    if (!location.pathname.startsWith('/loja/')) {
      clearLastPassbookId();
      passbookCache.current = null;
    }
  }, [inspectsStoreOnlyWhenInUrl, location.pathname]);

  // Ao abrir o menu mobile, rola até o item da página atual.
  useEffect(() => {
    if (!mobileOpen) return;
    const timer = window.setTimeout(() => {
      const active = mobileNavScrollRef.current?.querySelector<HTMLElement>('[aria-current="page"]');
      active?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 280);
    return () => window.clearTimeout(timer);
  }, [mobileOpen, location.pathname]);

  const { data: fetchedPassbook, isError: passbookFetchError } = usePassbookQuery(passbookIdForFetch, {
    enabled: Boolean(passbookIdForFetch),
  });
  const { data: npsMe } = useNpsMe({ enabled: isNpsSession(authSource) });

  const passbookReady =
    Boolean(passbookIdForFetch) &&
    Boolean(fetchedPassbook) &&
    String(fetchedPassbook!._id) === passbookIdForFetch;

  if (fetchedPassbook && passbookReady && passbookIdForFetch) {
    passbookCache.current = { id: passbookIdForFetch, data: fetchedPassbook };
  }

  // Query 404: não reutilizar cache nem ID morto no menu.
  if (passbookFetchError && passbookCache.current?.id === passbookIdForFetch) {
    passbookCache.current = null;
  }

  const passbook = passbookReady
    ? fetchedPassbook!
    : !passbookFetchError && passbookCache.current?.id === passbookIdForFetch
      ? passbookCache.current.data
      : null;

  const { brand } = useBrand();
  // Na URL da Fixe a marca não segue o partner da loja. White-label só no host do parceiro.
  useSyncBrandFromPassbook(
    passbook && user?.role !== 'Admin'
      ? { partner: passbook.partner, partnerName: passbook.partnerName }
      : null,
  );

  if (!user) return null;

  const idPassbook = passbook ? passbookIdForFetch || undefined : undefined;

  const npsIsAdmin = isNpsMasterEmail(user.email) || isNpsMasterEmail(npsMe?.email);

  const items = buildNavigation(user, idPassbook, withUserPageFlags(passbook, user), npsIsAdmin, authSource);
  const indexPath = idPassbook ? `/loja/${idPassbook}` : undefined;
  const tenantName = passbook?.organizationName ?? brand.displayName;

  // Inspeção = Admin dentro de uma loja específica (rota /loja/:id, não o fallback).
  // Só nesse caso exibimos o header — com a marca (logo + cores) do cliente.
  const isInspecting = user.role === 'Admin' && Boolean(urlPassbookId);

  const togglePin = () => {
    setSidebarPinnedState((prev) => {
      const next = !prev;
      setSidebarPinned(next);
      return next;
    });
  };

  const sidebarBody = (options?: {
    onNavigate?: () => void;
    collapsed?: boolean;
    showPin?: boolean;
    navScrollRef?: RefObject<HTMLDivElement | null>;
  }) => (
    // px-[15px] + 1px de borda do glass: no trilho de 72px sobra a coluna de 40px dos ícones.
    <div className="flex h-full flex-col gap-6 px-[15px] pb-[max(1rem,env(safe-area-inset-bottom))] pt-[max(1rem,env(safe-area-inset-top))]">
      <SidebarHeader pinned={sidebarPinned} onTogglePin={togglePin} showPin={options?.showPin ?? true} />
      <BrandSwitcher currentId={idPassbook} onNavigate={options?.onNavigate} />
      {/* -mr/pr: a barra de rolagem encosta na borda direita da sidebar, fora do padding. */}
      <div ref={options?.navScrollRef} className="-mr-[15px] flex-1 overflow-y-auto overflow-x-hidden pr-[15px]">
        <SidebarNav items={items} indexPath={indexPath} onNavigate={options?.onNavigate} />
      </div>
      <div className="shrink-0 border-t border-border/60 pt-3">
        <SidebarQuota passbook={passbook} passbookId={idPassbook} collapsed={options?.collapsed} />
        <UserMenu
          collapsed={options?.collapsed}
          showNotices={passbook?.hasEmailMarketing === true || passbook?.hasSmsMarketing === true}
        />
      </div>
    </div>
  );

  return (
      <div className="flex min-h-full">
      <QuotaNoticeSync passbook={passbook} passbookId={idPassbook} />
      {/* Sidebar desktop. O wrapper reserva o espaço no layout (fixada = aberta,
          desafixada = trilho); o aside dentro dele abre por cima do conteúdo. */}
      <motion.div
        initial={false}
        animate={{ width: sidebarPinned ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED }}
        transition={reduceMotion ? { duration: 0 } : SIDEBAR_SPRING}
        className="sticky top-0 z-40 hidden h-screen shrink-0 lg:block"
      >
        {/* Sombra só enquanto flutua sobre a página (desafixada e aberta no hover). */}
        <motion.div
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-y-0 left-0 shadow-[16px_0_40px_-20px_hsl(var(--glass-shadow)/0.45)] transition-opacity duration-300',
            sidebarFloating ? 'opacity-100' : 'opacity-0',
          )}
          style={{ width: sidebarWidth }}
        />
        <motion.aside
          ref={sidebarRef}
          {...sidebarHover.handlers}
          style={{ width: sidebarWidth }}
          className="glass absolute inset-y-0 left-0 flex flex-col overflow-hidden"
        >
          <SidebarMotionProvider progress={sidebarProgress}>
            {sidebarBody({ collapsed: !sidebarExpanded })}
          </SidebarMotionProvider>
        </motion.aside>
      </motion.div>

      {/* Drawer mobile — sempre expandido */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-background/60 backdrop-blur-sm lg:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', stiffness: 320, damping: 34 }}
              className="glass-elevated fixed inset-0 z-50 w-full lg:hidden"
            >
              <button
                type="button"
                className="absolute right-3 top-3 grid size-9 place-items-center rounded-lg text-muted-foreground hover:bg-accent/60"
                onClick={() => setMobileOpen(false)}
                aria-label="Fechar menu"
              >
                <X className="size-5" />
              </button>
              {sidebarBody({
                onNavigate: () => setMobileOpen(false),
                collapsed: false,
                showPin: false,
                navScrollRef: mobileNavScrollRef,
              })}
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Conteúdo */}
      <div
        className={cn(
          'flex min-h-screen min-w-0 flex-1 flex-col',
          isFullBleedCanvas && 'h-dvh min-h-0 overflow-hidden',
        )}
      >
        {viewAs ? (
          <div className="z-40 flex items-center gap-3 bg-warning/15 px-4 py-1.5 text-sm text-warning lg:px-8" role="status">
            <Eye className="size-4 shrink-0" aria-hidden />
            <span className="min-w-0 truncate">
              Vendo como <strong className="font-semibold">{viewAs.name || viewAs.email}</strong> · {viewAs.role}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto shrink-0 text-warning hover:text-warning"
              onClick={() => {
                stopViewAs();
                navigate('/clientes');
              }}
            >
              Sair da visão
            </Button>
          </div>
        ) : null}
        {isInspecting ? (
          // Header de inspeção — marca (logo + cores) do cliente + chip de modo inspeção.
          <header
            className={cn(
              'sticky top-0 z-30 flex h-16 items-center gap-3 px-4 lg:px-8',
              passbook?.backgroundColor ? 'border-b border-black/10' : 'glass-thin',
            )}
            style={
              passbook?.backgroundColor
                ? {
                    backgroundColor: passbook.backgroundColor,
                    color: passbook.foregroundColor ?? undefined,
                  }
                : undefined
            }
          >
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileOpen(true)}
              aria-label="Abrir menu"
            >
              <Menu className="size-5" />
            </Button>
            {urlPassbookId ? (
              <img
                src={passbookLogoUrl(urlPassbookId)}
                alt={tenantName}
                className="h-9 w-auto max-w-[140px] shrink-0 object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : null}
            <AdminInspectHeaderMark
              storeName={tenantName}
              onBrandColors={Boolean(passbook?.backgroundColor)}
            />
            <div className="min-w-0 flex-1" />
            {urlPassbookId && realUser && isMasterEmail(realUser.email) ? (
              <AdminInspectConfigureButton idPassbook={urlPassbookId} onBrandColors={Boolean(passbook?.backgroundColor)} />
            ) : null}
            <AdminInspectBackButton onBrandColors={Boolean(passbook?.backgroundColor)} />
          </header>
        ) : isAdmin && idPassbook && passbook ? (
          // Fora da loja, com inspeção guardada: faixa neutra para voltar à loja.
          <div className="glass-thin sticky top-0 z-30 flex h-12 items-center gap-3 px-4 lg:px-8">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)} aria-label="Abrir menu">
              <Menu className="size-5" />
            </Button>
            <AdminInspectHeaderMark storeName={tenantName} />
            <div className="min-w-0 flex-1" />
            <Button variant="secondary" size="sm" asChild>
              <Link to={`/loja/${idPassbook}`}>
                <ArrowLeft className="size-4" />
                <span className="hidden sm:inline">Voltar à loja</span>
              </Link>
            </Button>
          </div>
        ) : (
          // Sem inspeção: header removido. Mantém só o gatilho do menu no mobile
          // (no desktop a sidebar fixa já está sempre visível).
          <div className="sticky top-0 z-30 flex h-14 items-center px-4 lg:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileOpen(true)}
              aria-label="Abrir menu"
            >
              <Menu className="size-5" />
            </Button>
          </div>
        )}

        <main
          className={cn(
            'flex flex-1 flex-col overflow-x-hidden',
            isFullBleedCanvas
              ? 'min-h-0 overflow-hidden p-0'
              : cn('pb-6 lg:pb-8', isNpsArea ? 'px-0 pt-0' : 'px-4 pt-6 lg:px-8 lg:pt-8'),
          )}
        >
          {isNpsArea || isFullBleedCanvas ? (
            <RouteTransition />
          ) : (
            <div className="mx-auto w-full max-w-7xl">
              <RouteTransition />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
